// iter-169ze — Rich entitlements card for MyPlan.
//
// Renders three sections (each hidden when empty):
//   • Funciones — boolean feature flags the user has access to.
//   • Recursos  — numeric caps. For metered ones (e.g. Recibos IA / mes)
//                 we render a progress bar with used / limit / remaining.
//   • Servicios — included services (priority support, onboarding, etc.).
//
// Data shape mirrors `services/entitlements_view.entitlements_summary`.
import React from "react";
import { CheckCircle2, Gauge, BadgeCheck, ShoppingCart, Infinity as InfinityIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";

export default function PlanEntitlementsCard({ entitlements, lang = "es" }) {
  const isEs = lang !== "en";
  const T = {
    sectionFeatures: isEs ? "Funciones incluidas" : "Included features",
    sectionResources: isEs ? "Recursos y consumo" : "Resources & consumption",
    sectionServices: isEs ? "Servicios incluidos" : "Included services",
    unlimited: isEs ? "Sin límite" : "Unlimited",
    remaining: isEs ? "restantes" : "remaining",
    used: isEs ? "Usados" : "Used",
    buyMore: isEs ? "Comprar más" : "Buy more",
    empty: isEs ? "Tu plan actual no incluye nada extra todavía." : "Your current plan has nothing extra yet.",
  };

  const features = entitlements?.features || [];
  const resources = entitlements?.resources || [];
  const services = entitlements?.services || [];

  if (!features.length && !resources.length && !services.length) {
    return null;
  }

  return (
    <div
      data-testid="my-plan-entitlements-card"
      className="rounded-3xl border border-cream-300 bg-white p-7 lg:p-9"
    >
      <div className="grid lg:grid-cols-3 gap-8">
        {/* ── Funciones ──────────────────────────────────────────── */}
        {features.length > 0 && (
          <div data-testid="my-plan-features-section">
            <SectionHeader icon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />} label={T.sectionFeatures} count={features.length} />
            <ul className="mt-3 space-y-2">
              {features.map((f) => (
                <li
                  key={f.key}
                  data-testid={`my-plan-feature-${f.key}`}
                  className="flex items-start gap-2 text-sm text-slate-700"
                >
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>{isEs ? f.label_es : f.label_en}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Recursos ──────────────────────────────────────────── */}
        {resources.length > 0 && (
          <div data-testid="my-plan-resources-section">
            <SectionHeader icon={<Gauge className="h-3.5 w-3.5 text-amber-600" />} label={T.sectionResources} count={resources.length} />
            <ul className="mt-3 space-y-4">
              {resources.map((r) => (
                <ResourceRow key={r.key} resource={r} T={T} isEs={isEs} />
              ))}
            </ul>
          </div>
        )}

        {/* ── Servicios ─────────────────────────────────────────── */}
        {services.length > 0 && (
          <div data-testid="my-plan-services-section">
            <SectionHeader icon={<BadgeCheck className="h-3.5 w-3.5 text-violet-600" />} label={T.sectionServices} count={services.length} />
            <ul className="mt-3 space-y-2">
              {services.map((s) => (
                <li
                  key={s.key}
                  data-testid={`my-plan-service-${s.key}`}
                  className="flex items-start gap-2 text-sm text-slate-700"
                >
                  <BadgeCheck className="h-4 w-4 text-violet-600 shrink-0 mt-0.5" />
                  <span>{isEs ? s.label_es : s.label_en}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ icon, label, count }) {
  return (
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
      {icon}
      {label}
      <span className="ml-1 text-slate-400 font-mono-num">· {count}</span>
    </div>
  );
}

function ResourceRow({ resource, T, isEs }) {
  const { key, unlimited, limit, used, remaining } = resource;
  const label = isEs ? resource.label_es : resource.label_en;
  const isMetered = !unlimited && limit > 0 && typeof used === "number";
  const pct = isMetered && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const low = isMetered && remaining !== null && limit > 0 && remaining / limit < 0.2;
  const barColor = pct >= 85 ? "bg-rose-500" : pct >= 60 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <li data-testid={`my-plan-resource-${key}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-slate-800">{label}</span>
        {unlimited ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold">
            <InfinityIcon className="h-3 w-3" /> {T.unlimited}
          </span>
        ) : isMetered ? (
          <span className="text-xs text-slate-500 tabular-nums">
            <span className="text-slate-900 font-semibold font-mono-num">{used.toLocaleString()}</span>
            {" / "}{limit.toLocaleString()}
          </span>
        ) : (
          <span className="text-xs text-slate-500 tabular-nums font-mono-num">
            {limit.toLocaleString()}
          </span>
        )}
      </div>
      {isMetered && (
        <>
          <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full ${barColor}`} style={{ width: `${Math.max(2, pct)}%` }} />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className={`text-[11px] ${low ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
              {remaining?.toLocaleString()} {T.remaining}
            </span>
            {low && (
              <Link to="/pricing">
                <Button
                  size="sm" variant="outline"
                  className="h-7 text-[11px] gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid={`my-plan-resource-buy-${key}`}
                >
                  <ShoppingCart className="h-3 w-3" />
                  {T.buyMore}
                </Button>
              </Link>
            )}
          </div>
        </>
      )}
    </li>
  );
}
